// Single supplier summary card used in the search results grid.
// Click navigates to /suppliers/:id.

import { Link } from "react-router";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LastEditedBy } from "@/components/LastEditedBy";
import { withFrom } from "@/lib/nav";

interface SupplierCardProps {
  supplier: {
    id: string;
    slug: string;
    name: string;
    country: string | null;
    capabilities: string[] | null;
    status: string;
    recent_orders_count: number;
    recent_quotes_count: number;
    updated_at?: string | null;
    updated_by?: string | null;
    agent_id?: string | null;
  };
  listKey?: string;
}

// 2-letter country code → flag emoji.
function flagEmoji(country: string | null): string {
  if (!country || country.length !== 2) return "";
  const cc = country.toUpperCase();
  // Regional indicator symbols are at U+1F1E6 + (letter index).
  const A = 0x41; // 'A'
  const RI = 0x1F1E6;
  return String.fromCodePoint(
    RI + (cc.charCodeAt(0) - A),
    RI + (cc.charCodeAt(1) - A),
  );
}

export function SupplierCard({ supplier, listKey }: SupplierCardProps) {
  const caps = (supplier.capabilities ?? []).slice(0, 3);
  const overflow = (supplier.capabilities?.length ?? 0) - caps.length;

  const to = `/suppliers/${supplier.id}`;

  return (
    <Link to={listKey ? withFrom(to, listKey) : to} className="block">
      <Card className="h-full transition-colors hover:bg-muted/40 hover:border-primary/40">
        <CardContent className="flex h-full flex-col gap-3 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-base font-semibold">{supplier.name}</h3>
              <p className="truncate font-mono text-xs text-muted-foreground">
                {supplier.slug}
              </p>
            </div>
            {supplier.country && (
              <span className="text-sm" title={supplier.country}>
                {flagEmoji(supplier.country)} {supplier.country}
              </span>
            )}
          </div>

          {caps.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {caps.map((c) => (
                <Badge key={c} variant="outline" className="text-[10px]">
                  {c}
                </Badge>
              ))}
              {overflow > 0 && (
                <Badge variant="muted" className="text-[10px]">
                  +{overflow}
                </Badge>
              )}
            </div>
          )}

          <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
            <Badge variant={supplier.status === "active" ? "success" : "muted"}>
              {supplier.status}
            </Badge>
            <span>
              {supplier.recent_orders_count} order
              {supplier.recent_orders_count === 1 ? "" : "s"} ·{" "}
              {supplier.recent_quotes_count} quote
              {supplier.recent_quotes_count === 1 ? "" : "s"}
            </span>
          </div>
          <div className="border-t pt-2">
            <LastEditedBy
              updated_at={supplier.updated_at}
              updated_by={supplier.updated_by}
              agent_id={supplier.agent_id}
            />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
